import { Grid, Flex, Button, Range } from "src/elements";
import {
  relaxFactorDataPointsFromZeroToOne,
  relaxFactorDataPointsFromZeroToInfinity,
  relaxFactorDataPointsSpaceMix,
} from "src/modals/StorePlanogramRules/store/relaxFactorDataPoints";
import { RulesSectionContainer } from "./components/RulesSectionContainer";
import { defaultPspRules } from "src/pages/Merchflows_merchflowId_BasePog_basePogId_/store/types";
import { useModals } from "../Modals";
import { RangeDatapoints } from "src/elements/RangeDatapoints/RangeDatapoints";

interface Props {
  isDisabled?: boolean;
  spaceMix: number;
  setSpaceMix: (value: number) => void;
  eitherOr: number | string;
  setEitherOr: (value: number | string) => void;
  bothOrNone: number | string;
  setBothOrNone: (value: number | string) => void;
  preReq: number | string;
  setPreReq: (value: number | string) => void;
  shelfItemCount: number | string;
  setShelfItemCount: (value: number | string) => void;
  fixedNotchNum: number | string;
  setFixedNotchNum: (value: number | string) => void;
  fingerSpace: number | string;
  setFingerSpace: (value: number | string) => void;
  unitsNotEnoughDos: number;
  setUnitsNotEnoughDos: (value: number) => void;
  unitsNotEnoughMos: number;
  setUnitsNotEnoughMos: (value: number) => void;
  unitsTooMany: number;
  setUnitsTooMany: (value: number) => void;
  facingsNotEnough: number | string;
  setFacingsNotEnough: (value: number | string) => void;
  facingsTooMany: number | string;
  setFacingsTooMany: (value: number | string) => void;
  rowsNotEnough: number | string;
  setRowsNotEnough: (value: number | string) => void;
  minWidth: number | string;
  setMinWidth: (value: number | string) => void;
  minDistribution: number;
  setMinDistribution: (value: number) => void;
  widthSlack: number | string;
  setWidthSlack: (value: number | string) => void;
  cdt1Contiguous: number | string;
  setCdt1Contiguous: (value: number | string) => void;
  cdt2Contiguous: number | string;
  setCdt2Contiguous: (value: number | string) => void;
  cdt3Contiguous: number | string;
  setCdt3Contiguous: (value: number | string) => void;
  cdt1Order: number | string;
  setCdt1Order: (value: number | string) => void;
  cdt2Order: number | string;
  setCdt2Order: (value: number | string) => void;
  cdt3Order: number | string;
  setCdt3Order: (value: number | string) => void;
  itemIdOrder: number | string;
  setItemIdOrder: (value: number | string) => void;
  itemSpan: number | string;
  setItemSpan: (value: number | string) => void;
  cdt1Span: number | string;
  setCdt1Span: (value: number | string) => void;
  cdt2Span: number | string;
  setCdt2Span: (value: number | string) => void;
  cdt3Span: number | string;
  setCdt3Span: (value: number | string) => void;
  secondaryOrientation: number | string;
  setSecondaryOrientation: (value: number | string) => void;
  shelfSegmentSpace: number;
  setShelfSegmentSpace: (value: number) => void;
}

export const OptimizationOptionsRules = ({
  isDisabled,
  spaceMix,
  setSpaceMix,
  eitherOr,
  setEitherOr,
  bothOrNone,
  setBothOrNone,
  preReq,
  setPreReq,
  shelfItemCount,
  setShelfItemCount,
  fixedNotchNum,
  setFixedNotchNum,
  fingerSpace,
  setFingerSpace,
  unitsNotEnoughDos,
  setUnitsNotEnoughDos,
  unitsNotEnoughMos,
  setUnitsNotEnoughMos,
  unitsTooMany,
  setUnitsTooMany,
  facingsNotEnough,
  setFacingsNotEnough,
  facingsTooMany,
  setFacingsTooMany,
  rowsNotEnough,
  setRowsNotEnough,
  minWidth,
  setMinWidth,
  minDistribution,
  setMinDistribution,
  widthSlack,
  setWidthSlack,
  cdt1Contiguous,
  setCdt1Contiguous,
  cdt2Contiguous,
  setCdt2Contiguous,
  cdt3Contiguous,
  setCdt3Contiguous,
  cdt1Order,
  setCdt1Order,
  cdt2Order,
  setCdt2Order,
  cdt3Order,
  setCdt3Order,
  itemIdOrder,
  setItemIdOrder,
  itemSpan,
  setItemSpan,
  cdt1Span,
  setCdt1Span,
  cdt2Span,
  setCdt2Span,
  cdt3Span,
  setCdt3Span,
  secondaryOrientation,
  setSecondaryOrientation,
  shelfSegmentSpace,
  setShelfSegmentSpace,
}: Props) => {
  const { openConfirmModal } = useModals();

  const confirmResetOptimizationOptions = () => {
    openConfirmModal({
      title: "Reset Optimization Options?",
      description:
        "Are you sure that you want to reset all optimization options values to their defaults?",
      buttons: [
        {
          id: "keep-current-values",
          label: "Keep Current Values",
          color: "primary",
          variant: "inverted",
        },
        {
          id: "reset-to-defaults",
          label: "Reset to Defaults",
          color: "yellow",
          variant: "default",
          onClick: resetOptimizationOptions,
        },
      ],
    });
  };

  const resetOptimizationOptions = () => {
    setSpaceMix(defaultPspRules.relax_factors.space_mix);
    setEitherOr(defaultPspRules.relax_factors.either_or);
    setBothOrNone(defaultPspRules.relax_factors.both_or_none);
    setPreReq(defaultPspRules.relax_factors.prereq);
    setShelfItemCount(defaultPspRules.relax_factors.shelf_item_count);
    setFixedNotchNum(defaultPspRules.relax_factors.fixed_notch_num);
    setFingerSpace(defaultPspRules.relax_factors.finger_space);
    setUnitsNotEnoughDos(defaultPspRules.relax_factors.units_not_enough_dos);
    setUnitsNotEnoughMos(defaultPspRules.relax_factors.units_not_enough_mos);
    setUnitsTooMany(defaultPspRules.relax_factors.units_too_many);
    setFacingsNotEnough(defaultPspRules.relax_factors.facings_not_enough);
    setFacingsTooMany(defaultPspRules.relax_factors.facings_too_many);
    setRowsNotEnough(defaultPspRules.relax_factors.rows_not_enough);
    setMinWidth(defaultPspRules.relax_factors.min_width);
    setMinDistribution(defaultPspRules.relax_factors.min_distribution);
    setWidthSlack(defaultPspRules.relax_factors.width_slack);
    setCdt1Contiguous(defaultPspRules.relax_factors.cdt1_contiguous);
    setCdt2Contiguous(defaultPspRules.relax_factors.cdt2_contiguous);
    setCdt3Contiguous(defaultPspRules.relax_factors.cdt3_contiguous);
    setCdt1Order(defaultPspRules.relax_factors.cdt1_order);
    setCdt2Order(defaultPspRules.relax_factors.cdt2_order);
    setCdt3Order(defaultPspRules.relax_factors.cdt3_order);
    setItemIdOrder(defaultPspRules.relax_factors.item_id_order);
    setItemSpan(defaultPspRules.relax_factors.item_span);
    setCdt1Span(defaultPspRules.relax_factors.cdt1_span);
    setCdt2Span(defaultPspRules.relax_factors.cdt2_span);
    setCdt3Span(defaultPspRules.relax_factors.cdt3_span);
    setSecondaryOrientation(defaultPspRules.relax_factors.secondary_orientation);
    setShelfSegmentSpace(defaultPspRules.relax_factors.shelf_segment_space);
  };

  return (
    <Grid
      areas={`
        "quantity-on-shelf  cdt-contiguity"
        "empty-space        cdt-contiguity"
        "others             others"
        "reset              reset"
      `}
      gap="20px"
    >
      <RulesSectionContainer
        title="Quantity on Shelf"
        area="quantity-on-shelf"
        isDisabled={isDisabled}
      >
        <RangeDatapoints
          value={unitsNotEnoughMos}
          setValue={setUnitsNotEnoughMos}
          title="units_not_enough_mos"
          tooltip="units_not_enough_mos"
          datapoints={relaxFactorDataPointsFromZeroToOne}
          leftLabel="Stick to input"
          rightLabel="Relax"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={unitsNotEnoughDos}
          setValue={setUnitsNotEnoughDos}
          title="units_not_enough_dos"
          tooltip="units_not_enough_dos"
          datapoints={relaxFactorDataPointsFromZeroToOne}
          leftLabel="Stick to input"
          rightLabel="Relax"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={facingsNotEnough}
          setValue={setFacingsNotEnough}
          title="facings_not_enough"
          tooltip="facings_not_enough"
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={facingsTooMany}
          setValue={setFacingsTooMany}
          title="facings_too_many"
          tooltip="facings_too_many"
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={rowsNotEnough}
          setValue={setRowsNotEnough}
          title="rows_not_enough"
          tooltip="Penalty for not having enough rows to meet min_rows. 0 = fully relaxed, ∞ = strictly enforced."
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />
      </RulesSectionContainer>

      <RulesSectionContainer
        title="CDT Contiguity / Ordering"
        area="cdt-contiguity"
        isDisabled={isDisabled}
      >
        <RangeDatapoints
          value={cdt1Contiguous}
          setValue={setCdt1Contiguous}
          title="cdt1_contiguous"
          tooltip="cdt1_contiguous"
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={cdt2Contiguous}
          setValue={setCdt2Contiguous}
          title="cdt2_contiguous"
          tooltip="cdt2_contiguous"
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={cdt3Contiguous}
          setValue={setCdt3Contiguous}
          title="cdt3_contiguous"
          tooltip="cdt3_contiguous"
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={cdt1Order}
          setValue={setCdt1Order}
          title="cdt1_order"
          tooltip="cdt1_order"
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={cdt2Order}
          setValue={setCdt2Order}
          title="cdt2_order"
          tooltip="cdt2_order"
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={cdt3Order}
          setValue={setCdt3Order}
          title="cdt3_order"
          tooltip="cdt3_order"
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />
      </RulesSectionContainer>

      <RulesSectionContainer title="Empty Space" area="empty-space" isDisabled={isDisabled}>
        <RangeDatapoints
          value={widthSlack}
          setValue={setWidthSlack}
          title="width_slack"
          tooltip="width_slack"
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />
      </RulesSectionContainer>

      <RulesSectionContainer title="Others" area="others" isDisabled={isDisabled}>
        <RangeDatapoints
          value={eitherOr}
          setValue={setEitherOr}
          title="either_or"
          tooltip="either_or"
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={bothOrNone}
          setValue={setBothOrNone}
          title="both_or_none"
          tooltip="both_or_none"
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={preReq}
          setValue={setPreReq}
          title="prereq"
          tooltip="prereq"
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={shelfItemCount}
          setValue={setShelfItemCount}
          title="shelf_item_count"
          tooltip="shelf_item_count"
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={fixedNotchNum}
          setValue={setFixedNotchNum}
          title="fixed_notch_num"
          tooltip="fixed_notch_num"
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={fingerSpace}
          setValue={setFingerSpace}
          title="finger_space"
          tooltip="finger_space"
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={unitsTooMany}
          setValue={setUnitsTooMany}
          title="units_too_many"
          tooltip="units_too_many"
          datapoints={relaxFactorDataPointsFromZeroToOne}
          leftLabel="Stick to input"
          rightLabel="Relax"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={minWidth}
          setValue={setMinWidth}
          title="min_width"
          tooltip="min_width"
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />

        <Range
          value={minDistribution}
          setValue={setMinDistribution}
          title="min_distribution"
          tooltip="min_distribution"
          min={0}
          max={100}
          step={1}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={itemIdOrder}
          setValue={setItemIdOrder}
          title="item_id_order"
          tooltip="item_id_order"
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={itemSpan}
          setValue={setItemSpan}
          title="item_span"
          tooltip="item_span"
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={cdt1Span}
          setValue={setCdt1Span}
          title="cdt1_span"
          tooltip="cdt1_span"
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={cdt2Span}
          setValue={setCdt2Span}
          title="cdt2_span"
          tooltip="cdt2_span"
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={cdt3Span}
          setValue={setCdt3Span}
          title="cdt3_span"
          tooltip="cdt3_span"
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={secondaryOrientation}
          setValue={setSecondaryOrientation}
          title="secondary_orientation"
          tooltip="secondary_orientation"
          datapoints={relaxFactorDataPointsFromZeroToInfinity}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={spaceMix}
          setValue={setSpaceMix}
          title="space_mix"
          tooltip="space_mix"
          datapoints={relaxFactorDataPointsSpaceMix}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />

        <RangeDatapoints
          value={shelfSegmentSpace}
          setValue={setShelfSegmentSpace}
          title="shelf_segment_space"
          tooltip="Penalty for not meeting the target shelf-segment space."
          datapoints={relaxFactorDataPointsSpaceMix}
          leftLabel="Relax"
          rightLabel="Stick to input"
          isDisabled={isDisabled}
        />
      </RulesSectionContainer>

      <Flex gridArea="reset" justify="center">
        <Button color="yellow" onClick={confirmResetOptimizationOptions}>
          Reset All Optimization Options
        </Button>
      </Flex>
    </Grid>
  );
};
